import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { updateProfile } from '@/services/user.service';
import { createInvitation } from '@/services/invitation.service';
import { inviteValueToRole } from '@/constants/accessLevels';
import { config } from '@/lib/config';
import { clearPostAuthRedirect } from '@/lib/postAuthRedirect';
import { useUserStore } from './userStore';

export type OnboardingStep =
  | 'founder-letter'
  | 'demo1'
  | 'demo2'
  | 'demo3'
  | 'demo4'
  | 'profile'
  | 'children'
  | 'collaborators'
  | 'forwarding'
  | 'digest'
  | 'summary';

export type ForwardingStep = 'choice' | 'auto-loading' | 'approve-senders';

/**
 * What the parent said they are here to do, on the fork that follows sign-in
 * (YAR-438). `null` until they choose; the fork renders while it is null.
 * Persisted like every other onboarding answer so a reload lands on the same
 * screen, and cleared with the rest of the store on reset or on a different
 * user signing in on the same device.
 */
export type EntryChoice = 'admin' | 'helper';

const STEP_ORDER: OnboardingStep[] = [
  'founder-letter',
  'demo1',
  'demo2',
  'demo3',
  'demo4',
  'profile',
  'children',
  'collaborators',
  'forwarding',
  'digest',
  'summary',
];

const STEP_PROGRESS: Record<OnboardingStep, number> = {
  'founder-letter': 0,
  demo1: 10,
  demo2: 20,
  demo3: 30,
  demo4: 40,
  profile: 50,
  children: 60,
  collaborators: 70,
  forwarding: 80,
  digest: 90,
  summary: 100,
};

/**
 * The finalized Admin SMS sequence is available whenever the shared SMS
 * foundation is enabled. Invitees use their role-specific flow and never walk
 * this store, so the step remains Admin-only without a second feature flag.
 */
function effectiveStepOrder(): OnboardingStep[] {
  return config.enableSms
    ? STEP_ORDER
    : STEP_ORDER.filter((step) => step !== 'digest');
}

/**
 * Maps a `currentStep` that has fallen out of the *effective* step order
 * back onto a valid step. This happens when a step gets spliced out by a
 * flag after being persisted — e.g. a rehydrated `currentStep: 'digest'`
 * after `VITE_ENABLE_SMS` flips off between sessions. `order.indexOf(step)`
 * then returns -1, and naively using that in `next()`/`back()` computes
 * `order[idx + 1]` / `order[idx - 1]` as `order[0]` / `order[-1]` — landing
 * on 'founder-letter' (next) or doing nothing (back), instead of resuming
 * near where the user actually was.
 *
 * Walks the FULL `STEP_ORDER` (which always contains every step, flag or
 * no flag) from the orphaned step's position, in `direction`, until it
 * finds a step that's still present in the effective `order`. This is
 * generic over which step got spliced out — it isn't hardcoded to
 * 'digest' — so it also covers any future flag-gated step.
 */
function resolveOrphanedStep(
  orphanedStep: OnboardingStep,
  order: OnboardingStep[],
  direction: 'forward' | 'backward',
): OnboardingStep {
  const fullIdx = STEP_ORDER.indexOf(orphanedStep);
  const step = direction === 'forward' ? 1 : -1;
  for (let i = fullIdx; i >= 0 && i < STEP_ORDER.length; i += step) {
    const candidate = STEP_ORDER[i];
    if (order.includes(candidate)) return candidate;
  }
  // Shouldn't happen while 'founder-letter'/'summary' stay unconditional
  // members of the flow, but fall back to the nearest boundary of `order`.
  return direction === 'forward' ? order[order.length - 1] : order[0];
}

/** The later of two steps in the full flow order (YAR-439 AC-9). */
function laterStep(a: OnboardingStep, b: OnboardingStep): OnboardingStep {
  return STEP_ORDER.indexOf(b) > STEP_ORDER.indexOf(a) ? b : a;
}

interface OnboardingFormData {
  displayName: string;
  avatarEmoji: string;
  avatarColor: string;
  timezone: string;
  role: string;
}

/**
 * What the server knows about this account's admin onboarding, read once at
 * mount by OnboardingEntry from the user and household stores (YAR-438).
 *
 * The step and the profile answers used to live only in this store's
 * localStorage copy, so a fresh browser context — QA closed an incognito
 * window and signed in again; a second device is the same case — restarted
 * the flow at the fork and the Founder's letter while the server still held
 * the users row (display name, role, timezone, avatar: the profile step
 * writes them) and the household. This is the server's side of that story,
 * used to seed a store that has never seen the account. A device that
 * already carries the flow keeps its own copy; see `initFromUser`.
 */
export interface ServerOnboardingProgress {
  /** `users.onboarding_step` — the last step this account rendered, on any device. */
  step: string | null;
  /**
   * The account already owns a household, i.e. it picked "I get the school
   * emails" before: the fork's admin choice, as the server remembers it.
   */
  ownsHousehold: boolean;
  /** The `users` row's profile columns, as the profile step wrote them. */
  profile: {
    displayName: string | null;
    role: string | null;
    timezone: string | null;
    avatarEmoji: string | null;
    avatarColor: string | null;
  };
}

/**
 * The last step write still in flight, so `completeOnboarding` can wait for
 * it: a `next()` write landing after the completion stamp would resurrect
 * the step on a finished account. Always settled — never rejects.
 */
let stepWriteInFlight: Promise<void> = Promise.resolve();

/**
 * Best-effort write of the step to `users.onboarding_step`, so another
 * device (or this one after its storage is gone) can resume here. Fire and
 * forget: this store's copy carries the flow, the server's only seeds a
 * device that has none, so a failed write must never surface — not offline,
 * not a bundle that outran the migration (web and edge do not deploy
 * atomically; the column may not exist yet), not a step the CHECK does not
 * know.
 */
function recordStepOnAccount(step: OnboardingStep): void {
  stepWriteInFlight = updateProfile({ onboarding_step: step }).then(
    () => {},
    () => {},
  );
}

/**
 * PostgREST's answer to a column its schema cache does not have — what a
 * web bundle gets from a backend the migration has not reached yet.
 * (`42703` is Postgres' own undefined_column, should the request ever get
 * that far.)
 */
function isUnknownColumnError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return code === 'PGRST204' || code === '42703';
}

/**
 * Maps a server step onto the effective order: a step the flag has turned
 * off resolves to the surviving step before it (the same rule `back()`
 * applies to an orphaned persisted step); a name this build does not know
 * is ignored.
 */
function resolveServerStep(step: string | null): OnboardingStep | null {
  if (!STEP_ORDER.includes(step as OnboardingStep)) return null;
  const order = effectiveStepOrder();
  return order.includes(step as OnboardingStep)
    ? (step as OnboardingStep)
    : resolveOrphanedStep(step as OnboardingStep, order, 'backward');
}

/**
 * Resolved outcome of the Gmail sender scan run by the 'approve-senders'
 * sub-step. Its transient 'loading' status is deliberately not part of this
 * union — a cached scan is by definition one that already finished, and
 * caching 'loading' would leave the step stuck on the spinner forever with
 * no scan in flight to resolve it.
 */
export type SenderScanStatus = 'ready' | 'empty' | 'error';

/** A scan result row. */
export interface SenderScanSuggestion {
  kind: 'suggested';
  /** Bare host for a domain, full lowercased address for an email. */
  value: string;
  isDomain: boolean;
  selected: boolean;
  /**
   * Human-readable sender name for platform-relayed senders — e.g. the
   * school behind `@bounces.parentsquare.com` (PR #223). Display-only:
   * rendered above `value` in the list, never saved.
   */
  displayName?: string;
}

/** A sender the user typed in the "Add more senders" sub-flow. */
export interface SenderScanManualEntry {
  kind: 'manual';
  /** Bare host for a domain, full lowercased address for an email. */
  value: string;
  isDomain: boolean;
}

/**
 * Cached state of the 'approve-senders' sub-step (YAR-284 AC16).
 *
 * The step's scan results and checkbox state used to live only in component
 * state, so advancing to the 'digest' step — which unmounts it — threw both
 * away: coming back re-ran the Gmail scan and rebuilt every row unselected.
 * Lifting it here (rather than into a prop drilled through OnboardingFlow)
 * keeps the shape alongside the rest of the flow's resumable state, and
 * means an accidental mid-onboarding reload restores the picks too.
 *
 * These shapes mirror `SenderListItem` in
 * apps/web/src/pages/onboarding/SenderList.tsx — StepApproveSenders aliases
 * them rather than declaring its own copies, so the cache can never drift
 * from what the list renders.
 */
export interface SenderScanCache {
  status: SenderScanStatus;
  suggestions: SenderScanSuggestion[];
  manual: SenderScanManualEntry[];
}

/**
 * An invitation the Admin has composed but that has NOT been sent.
 *
 * `role` is the invite dropdown's value ('edit' / 'view'), mapped to a
 * caregiver_role only at send time, exactly as the step used to do inline.
 */
export interface PendingInvitation {
  id: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Where the held invitations live between page loads.
 *
 * `sessionStorage`, deliberately, and it is the one storage decision in this
 * file that is not the `persist` middleware's. Three requirements pull in
 * different directions:
 *
 *  - In memory only loses the list on a refresh. An Admin who reloads
 *    mid-onboarding would find the people they just typed gone, which reads as
 *    the app having forgotten them.
 *  - `localStorage` keeps it after the browser closes — including for the
 *    Admin who abandons onboarding and never returns, which is exactly the
 *    population this feature exists for. Their family's contacts would then
 *    sit on that disk indefinitely, and the store already refuses that for
 *    `senderScan` on the same grounds.
 *  - `sessionStorage` survives the refresh and is gone when the tab is, which
 *    is the shape of the actual need: finishing what you started in one sitting.
 *
 * Every access is wrapped: Safari private mode and "block all cookies" make
 * these throw rather than return null, and losing a draft invitation list is
 * never worth taking the onboarding flow down with it.
 */
const HELD_INVITATIONS_KEY = 'yaruta_onboarding_held_invites';

interface HeldInvitations {
  invitations: PendingInvitation[];
  householdId: string | null;
}

/** Shape-checked on read: this is parsed from storage the user can edit. */
export function readHeldInvitations(): HeldInvitations {
  const empty: HeldInvitations = { invitations: [], householdId: null };
  try {
    const raw = sessionStorage.getItem(HELD_INVITATIONS_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<HeldInvitations>;
    if (!Array.isArray(parsed?.invitations)) return empty;
    return {
      invitations: parsed.invitations.filter(
        (i): i is PendingInvitation =>
          typeof i?.id === 'string' &&
          typeof i?.email === 'string' &&
          typeof i?.name === 'string' &&
          typeof i?.role === 'string',
      ),
      householdId:
        typeof parsed.householdId === 'string' ? parsed.householdId : null,
    };
  } catch {
    return empty;
  }
}

function writeHeldInvitations(held: HeldInvitations): void {
  try {
    if (held.invitations.length === 0) {
      sessionStorage.removeItem(HELD_INVITATIONS_KEY);
      return;
    }
    sessionStorage.setItem(HELD_INVITATIONS_KEY, JSON.stringify(held));
  } catch {
    // Storage unavailable or full. The in-memory copy still carries the flow.
  }
}

interface OnboardingStoreState {
  userId: string | null;
  currentStep: OnboardingStep;
  /**
   * The furthest step this flow has reached (YAR-439 AC-9). Back navigation
   * may walk all the way to the fork, but re-entering the admin path ("Set up
   * my family instead", or re-picking it on the fork) must resume here — not
   * at the Founder's letter where Back happened to leave `currentStep`.
   */
  furthestStep: OnboardingStep;
  forwardingStep: ForwardingStep;
  formData: OnboardingFormData;
  progress: number;
  canGoBack: boolean;
  /** Null until the sender step's scan resolves; see `SenderScanCache`. */
  senderScan: SenderScanCache | null;
  /**
   * Invitations composed on the collaborators step and held until the Admin
   * completes onboarding (YAR-435).
   *
   * They used to be sent the moment the button was pressed, so abandoning the
   * flow half-way still mailed people an invite into a household whose setup
   * was never finished — and the invitee arrived before the senders, children
   * or forwarding existed. Holding them means an unfinished flow simply never
   * sends, which is what a draft should do.
   */
  pendingInvitations: PendingInvitation[];
  /**
   * The household the held invitations belong to, captured on the
   * collaborators step where it is already resolved.
   *
   * Stored rather than re-derived at completion so the send does not depend on
   * a second lookup succeeding at the very last step of onboarding.
   */
  pendingInvitationsHouseholdId: string | null;
  /**
   * The collaborators step's working copy of the list, while it has edits the
   * Admin has not saved; `null` when there are none (the step then shows
   * `pendingInvitations`).
   *
   * Kept apart from `pendingInvitations` because only "Save invites" commits
   * (YAR-544). Rows typed here used to go straight into the held queue, so an
   * Admin who filled someone in and then tapped "I'll do this later" still saw
   * them on the Summary and had them invited. "I'll do this later" now drops
   * this draft and leaves whatever was saved before untouched.
   *
   * In memory only: it survives the step unmounting (Back and returning), but
   * not a reload — unsaved input is exactly what a reload may lose, and it is
   * not worth writing a family's contacts to storage for.
   */
  draftInvitations: PendingInvitation[] | null;
  entryChoice: EntryChoice | null;
}

interface OnboardingStoreActions {
  next: () => void;
  back: () => void;
  setFormData: (partial: Partial<OnboardingFormData>) => void;
  setEntryChoice: (choice: EntryChoice | null) => void;
  /** Moves `currentStep` forward to `furthestStep`; never moves backward. */
  resumeAtFurthestStep: () => void;
  setForwardingStep: (step: ForwardingStep) => void;
  setSenderScan: (scan: SenderScanCache | null) => void;
  setPendingInvitations: (invitations: PendingInvitation[]) => void;
  setHouseholdIdForInvites: (householdId: string | null) => void;
  /** Replaces the collaborators step's unsaved working copy. */
  setDraftInvitations: (invitations: PendingInvitation[]) => void;
  /** "Save invites": the working copy becomes the held queue. */
  saveDraftInvitations: () => void;
  /** "I'll do this later": drops the working copy; the held queue stays. */
  discardDraftInvitations: () => void;
  /** Sends the held invitations; resolves with the ones that failed. */
  sendPendingInvitations: () => Promise<PendingInvitation[]>;
  /**
   * Binds the store to the signed-in account. `server` seeds a store that has
   * never seen this account (first visit on this device, or storage wiped);
   * it is ignored when the store already carries this user's flow.
   */
  initFromUser: (
    userId: string,
    detectedTimezone: string,
    server?: ServerOnboardingProgress,
  ) => void;
  completeOnboarding: () => Promise<void>;
  resetStore: () => void;
}

const initialFormData: OnboardingFormData = {
  displayName: '',
  avatarEmoji: '',
  avatarColor: '',
  timezone: '',
  role: '',
};

const initialState: OnboardingStoreState = {
  userId: null,
  currentStep: 'founder-letter',
  furthestStep: 'founder-letter',
  forwardingStep: 'choice',
  formData: initialFormData,
  progress: STEP_PROGRESS['founder-letter'],
  canGoBack: false,
  senderScan: null,
  pendingInvitations: [],
  pendingInvitationsHouseholdId: null,
  draftInvitations: null,
  entryChoice: null,
};

/**
 * Migrates a persisted onboarding store payload to the current schema.
 *
 * v1 step keys ('security', old positions) don't map cleanly onto the v2
 * flow — rather than guess a corresponding v2 step, we restart the flow UI
 * but keep the user's typed profile data (and userId) so they don't have to
 * re-enter it.
 *
 * Exported for direct unit testing.
 */
export const migrateOnboardingState = (
  persisted: unknown,
  version: number,
): OnboardingStoreState => {
  if (version < 2) {
    const old = persisted as Partial<OnboardingStoreState> | undefined;
    return {
      ...initialState,
      userId: old?.userId ?? null,
      formData: { ...initialFormData, ...(old?.formData ?? {}) },
    };
  }
  return persisted as OnboardingStoreState;
};

export const useOnboardingStore = create<
  OnboardingStoreState & OnboardingStoreActions
>()(
  persist(
    (set, get) => ({
      ...initialState,
      // Rehydrated from sessionStorage rather than from the `persist`
      // middleware, which writes to localStorage and deliberately excludes
      // these two. Read here, at store construction, so an Admin who reloads
      // mid-onboarding finds the people they typed still listed — and NOT
      // inside `initialState`, which `resetStore` reuses and which would then
      // resurrect a queue that completing had just cleared.
      ...(() => {
        const held = readHeldInvitations();
        return {
          pendingInvitations: held.invitations,
          pendingInvitationsHouseholdId: held.householdId,
        };
      })(),
      next: () => {
        const { currentStep } = get();
        const order = effectiveStepOrder();
        const idx = order.indexOf(currentStep);
        if (idx === -1) {
          // Orphaned step (see resolveOrphanedStep) — e.g. a persisted
          // 'digest' step after VITE_ENABLE_SMS flips off. Land on the
          // nearest surviving step *after* it (forwarding -> summary)
          // instead of order[idx + 1] === order[0] ('founder-letter').
          const nextStep = resolveOrphanedStep(currentStep, order, 'forward');
          set({
            furthestStep: laterStep(get().furthestStep, nextStep),
            currentStep: nextStep,
            progress: STEP_PROGRESS[nextStep],
            canGoBack: order.indexOf(nextStep) > 0,
          });
          recordStepOnAccount(nextStep);
          return;
        }
        if (idx < order.length - 1) {
          const nextStep = order[idx + 1];
          set({
            furthestStep: laterStep(get().furthestStep, nextStep),
            currentStep: nextStep,
            progress: STEP_PROGRESS[nextStep],
            canGoBack: order.indexOf(nextStep) > 0,
          });
          recordStepOnAccount(nextStep);
        }
      },
      back: () => {
        const { currentStep } = get();
        const order = effectiveStepOrder();
        const idx = order.indexOf(currentStep);
        if (idx === -1) {
          // Orphaned step (see resolveOrphanedStep) — without this, the
          // `idx > 0` guard below is never true for idx === -1, so back()
          // would silently no-op, freezing navigation on the stale step.
          const prevStep = resolveOrphanedStep(currentStep, order, 'backward');
          set({
            currentStep: prevStep,
            progress: STEP_PROGRESS[prevStep],
            canGoBack: order.indexOf(prevStep) > 0,
          });
          recordStepOnAccount(prevStep);
          return;
        }
        if (idx > 0) {
          const prevStep = order[idx - 1];
          set({
            currentStep: prevStep,
            progress: STEP_PROGRESS[prevStep],
            canGoBack: order.indexOf(prevStep) > 0,
            // Reset forwarding sub-flow so re-entering 'forwarding' starts
            // from 'choice' — and drop the cached scan with it, since that
            // re-entry goes through a fresh Gmail connect (see
            // `setForwardingStep`). Leaving 'digest' backwards is the case
            // this must NOT touch: currentStep is 'digest' there, so both
            // the sub-step and its cache survive (YAR-284 AC16).
            ...(currentStep === 'forwarding' && {
              forwardingStep: 'choice' as const,
              senderScan: null,
            }),
          });
          recordStepOnAccount(prevStep);
        }
      },
      setFormData: (partial) =>
        set((state) => ({
          formData: { ...state.formData, ...partial },
        })),
      setEntryChoice: (choice) => set({ entryChoice: choice }),
      resumeAtFurthestStep: () => {
        const { currentStep, furthestStep } = get();
        const order = effectiveStepOrder();
        const target = order.includes(furthestStep)
          ? furthestStep
          : resolveOrphanedStep(furthestStep, order, 'backward');
        if (STEP_ORDER.indexOf(target) <= STEP_ORDER.indexOf(currentStep)) {
          return;
        }
        set({
          currentStep: target,
          progress: STEP_PROGRESS[target],
          canGoBack: order.indexOf(target) > 0,
        });
        recordStepOnAccount(target);
      },
      setForwardingStep: (step) =>
        set({
          forwardingStep: step,
          progress: step === 'approve-senders' ? 90 : 80,
          // Any move OFF the sender step within the forwarding sub-flow
          // invalidates its cached scan: 'choice' (the step's own Back, or a
          // blocked mailbox verdict) and 'auto-loading' both precede a Gmail
          // connect, which may be a *different* mailbox. Replaying the
          // previous account's senders there would be wrong, so the next
          // visit re-scans. Advancing to 'digest' does not come through
          // here, so AC16's cache survives.
          ...(step !== 'approve-senders' && { senderScan: null }),
        }),
      setSenderScan: (scan) => set({ senderScan: scan }),
      setPendingInvitations: (invitations) => {
        set({ pendingInvitations: invitations });
        writeHeldInvitations({
          invitations,
          householdId: get().pendingInvitationsHouseholdId,
        });
      },
      setDraftInvitations: (invitations) =>
        set({ draftInvitations: invitations }),
      saveDraftInvitations: () => {
        const { draftInvitations } = get();
        if (draftInvitations === null) return;
        get().setPendingInvitations(draftInvitations);
        set({ draftInvitations: null });
      },
      discardDraftInvitations: () => set({ draftInvitations: null }),
      setHouseholdIdForInvites: (householdId) => {
        set({ pendingInvitationsHouseholdId: householdId });
        writeHeldInvitations({
          invitations: get().pendingInvitations,
          householdId,
        });
      },

      /**
       * Send the held invitations. Returns the ones that did NOT go out.
       *
       * Separated from `completeOnboarding` because it is the only part of
       * finishing that can partly fail, and because completion ends in a hard
       * navigation — anything that wants to react to a failure has to do it
       * before that, which means before this returns to its caller.
       *
       * `allSettled`, not `all`: one invitee who already belongs to another
       * household must not stop the others from being invited.
       */
      sendPendingInvitations: async () => {
        const { pendingInvitations, pendingInvitationsHouseholdId } = get();
        if (pendingInvitations.length === 0) return [];
        // No household means every send would fail identically. Report them
        // all as failed rather than calling, and above all do not clear them:
        // the list is the only copy of what the Admin typed.
        if (!pendingInvitationsHouseholdId) return pendingInvitations;

        const results = await Promise.allSettled(
          pendingInvitations.map((invitation) =>
            createInvitation(pendingInvitationsHouseholdId, {
              invitee_email: invitation.email,
              invitee_name: invitation.name,
              role: inviteValueToRole(invitation.role),
            }),
          ),
        );
        const failed = pendingInvitations.filter(
          (_, i) => results[i].status === 'rejected',
        );
        // Only the ones that went out are dropped, so a retry never mails
        // someone twice — and that has to reach storage too, or a reload would
        // resurrect an invitation that was already delivered.
        set({ pendingInvitations: failed });
        writeHeldInvitations({
          invitations: failed,
          householdId: pendingInvitationsHouseholdId,
        });
        return failed;
      },
      initFromUser: (userId, detectedTimezone, server) => {
        const state = get();
        // The store already carries this account's flow on this device: it
        // is the authoritative copy, the server's is not consulted. (The
        // server only ever holds the step and the profile; this store also
        // holds the sub-step, the scan cache and the held invitations, none
        // of which a server seed could restore — so a device that has them
        // must not be reset to the server's flatter picture.)
        if (state.userId === userId) {
          set((s) => ({
            userId,
            formData: {
              ...s.formData,
              // Only seed timezone if not already set by user
              timezone: s.formData.timezone || detectedTimezone,
            },
          }));
          return;
        }
        // A different account's flow is theirs: drop it. An ownerless store
        // (fresh device, wiped storage, a v1 payload that carried no userId)
        // keeps whatever it holds. Either way the server fills the gaps —
        // only the gaps, so nothing typed on this device is overwritten.
        const base = state.userId === null ? state : initialState;
        const step =
          base.currentStep === initialState.currentStep && server
            ? resolveServerStep(server.step)
            : null;
        const order = effectiveStepOrder();
        set({
          ...(state.userId !== null && initialState),
          userId,
          ...(step && {
            currentStep: step,
            furthestStep: laterStep(
              base.furthestStep ?? 'founder-letter',
              step,
            ),
            progress: STEP_PROGRESS[step],
            canGoBack: order.indexOf(step) > 0,
          }),
          // A household on the server IS the admin choice — the fork is
          // where it gets created. OnboardingEntry still re-checks the
          // inverse at mount (a remembered 'admin' with no household).
          entryChoice:
            base.entryChoice ?? (server?.ownsHousehold ? 'admin' : null),
          formData: {
            displayName:
              base.formData.displayName || server?.profile.displayName || '',
            // Not seeded from the server: the family role is per household
            // and the profile step reads it from the membership it is for.
            // The account-level value could be another household's (YAR-485).
            role: base.formData.role || '',
            timezone:
              base.formData.timezone ||
              server?.profile.timezone ||
              detectedTimezone,
            avatarEmoji:
              base.formData.avatarEmoji || server?.profile.avatarEmoji || '',
            avatarColor:
              base.formData.avatarColor || server?.profile.avatarColor || '',
          },
        });
      },
      completeOnboarding: async () => {
        const { formData } = get();
        // Send what the collaborators step held back (YAR-435). Before the
        // profile write, because completion ends in `resetStore()` and a hard
        // navigation — after that point the queue no longer exists to send.
        //
        // The result is deliberately not acted on. A failure neither blocks
        // completion nor is surfaced: the Admin has finished their part, and an
        // invitee who already belongs to another household is not something
        // they could resolve from the last screen of onboarding anyway. Decided
        // explicitly rather than by omission — the alternatives (warn on the
        // summary before completing, or carry a one-shot notice across the
        // navigation into /today) were considered and turned down.
        //
        // The recovery is Settings > Members, which lists who is actually
        // invited, so a missing invitee is visible and can be re-invited there.
        //
        // If you are here because someone reported "I invited them and they
        // never got it", this line is where that silence comes from.
        await get().sendPendingInvitations();
        // profile fields (display_name, avatar_emoji, timezone) are already written to DB
        // by StepProfileSetup on step completion (Task 9). We only need to mark onboarding complete.
        //
        // The account-level resume point is cleared in the SAME write: two
        // independent round trips could land in either order and leave a
        // finished account carrying 'summary', which a later re-run (an
        // admin nulling `onboarding_completed_at` to re-test) would resume
        // at. The write `next()` fired on arriving here is awaited first for
        // the same reason. If the backend does not know the column yet (a
        // bundle that outran the migration by minutes), fall back to the
        // plain stamp — finishing must never fail for a bookkeeping column.
        await stepWriteInFlight;
        const completedAt = new Date().toISOString();
        try {
          await updateProfile({
            onboarding_completed_at: completedAt,
            onboarding_step: null,
          });
        } catch (err) {
          if (!isUnknownColumnError(err)) throw err;
          await updateProfile({ onboarding_completed_at: completedAt });
        }
        useUserStore.getState().updateProfile({
          onboardingCompletedAt: completedAt,
          ...(formData.displayName && { displayName: formData.displayName }),
          ...(formData.avatarEmoji && { avatarEmoji: formData.avatarEmoji }),
          ...(formData.avatarColor && { avatarColor: formData.avatarColor }),
          ...(formData.timezone && { timezone: formData.timezone }),
        });
        get().resetStore();
        // A fresh user finishing onboarding hard-navigates to /today, which
        // bypasses the redirect consumer in Onboarding.tsx. Clear the stash
        // here so a stale deep link can't hijack a later same-tab visit to '/'.
        clearPostAuthRedirect();
        window.location.replace('/today');
      },
      resetStore: () => {
        writeHeldInvitations({ invitations: [], householdId: null });
        set(initialState);
      },
    }),
    {
      name: 'yaruta_onboarding_v2', // keep key; version the schema instead
      version: 2,
      migrate: migrateOnboardingState,
      // `senderScan` is deliberately NOT persisted. It holds sender domains and
      // addresses derived from a live scan of the parent's Gmail, and there is
      // no reason for that to sit on disk — AC16 only needs it to survive the
      // digest step unmounting the sender step, and the store is a module-level
      // singleton, so in-memory state already covers that. Keeping it out of
      // localStorage means a shared or lost device never carries a readable
      // list of who mails this family.
      // `pendingInvitations` is excluded for the same reason as `senderScan`
      // and stated once here: it holds the names and email addresses of the
      // people this family is inviting. It only has to survive one step
      // unmounting, which a module-level singleton already covers, so the cost
      // of writing it to disk buys nothing. The household id alongside it is
      // not sensitive, but it is meaningless without the list, so it goes too.
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              key !== 'senderScan' &&
              key !== 'pendingInvitations' &&
              key !== 'draftInvitations' &&
              key !== 'pendingInvitationsHouseholdId',
          ),
        ) as OnboardingStoreState & OnboardingStoreActions,
      // Belt-and-suspenders alongside the idx===-1 handling in next()/back():
      // a persisted `currentStep` can fall out of the effective step order
      // between sessions (e.g. 'digest' surviving a rehydrate after
      // VITE_ENABLE_SMS flips off). Clamping here (rather than in
      // onRehydrateStorage) means the fix runs synchronously on EVERY
      // hydration, including the very first page load: localStorage-backed
      // persist hydrates synchronously inside create() itself, before the
      // top-level `useOnboardingStore` binding is initialized, so an
      // onRehydrateStorage callback referencing that const throws a
      // (silently swallowed) TDZ ReferenceError on initial load — which also
      // freezes `persist.hasHydrated()` at false forever. `merge` has no
      // such dependency: it receives `current` straight from `create()`'s
      // initializer, so it's safe to run inline during the very first sync
      // hydration.
      merge: (persisted, current) => {
        const saved = persisted as Partial<OnboardingStoreState>;
        const spread = { ...current, ...saved };
        // An onboarding persisted before `furthestStep` existed (YAR-439
        // AC-9) only knows `currentStep`; the furthest step is at least that,
        // so its first re-entry after the deploy resumes there too.
        const merged = {
          ...spread,
          furthestStep: laterStep(
            saved.furthestStep ?? 'founder-letter',
            spread.currentStep,
          ),
        };
        const order = effectiveStepOrder();
        if (order.includes(merged.currentStep)) return merged;
        // Orphaned persisted step — snap to 'forwarding', the last
        // unconditional step before any flag-gated one, so OnboardingFlow
        // never even renders a screen for a step the flag has turned off.
        return {
          ...merged,
          currentStep: 'forwarding',
          progress: STEP_PROGRESS.forwarding,
          canGoBack: order.indexOf('forwarding') > 0,
          forwardingStep: 'choice',
          // Snapping back to 'choice' means the next sender step follows a
          // fresh Gmail connect, so the persisted scan is stale — same
          // invalidation rule as `setForwardingStep`.
          senderScan: null,
        };
      },
    },
  ),
);
